import { InlineText, Stack, Text, useLocale, useT, type InlineRun } from "@bundle/ui";

/** The Terms of Service and Privacy Policy on the landing site (apps/site), in the app's language. */
export function legalUrls(locale: string): { terms: string; privacy: string } {
  const base = locale === "pt-BR" ? "https://bundle.direct/pt-br" : "https://bundle.direct";
  return { terms: `${base}/terms`, privacy: `${base}/privacy` };
}

function useLegalLinks(): { terms: InlineRun; privacy: InlineRun } {
  const t = useT();
  const urls = legalUrls(useLocale());
  return {
    terms: { text: t("legal.terms"), link: urls.terms },
    privacy: { text: t("legal.privacy"), link: urls.privacy },
  };
}

/** "By continuing, you agree to Bundle's Terms of Service and Privacy Policy." — under the sign-in options. */
export function LegalAgreement() {
  const t = useT();
  const links = useLegalLinks();
  // Format with markers, then split so each placeholder becomes its link run (word order differs per language).
  const runs = t("legal.agreement", { terms: "\u0000terms\u0000", privacy: "\u0000privacy\u0000" })
    .split("\u0000")
    .filter((s) => s !== "")
    .map((s): InlineRun => (s === "terms" ? links.terms : s === "privacy" ? links.privacy : { text: s }));
  return (
    <Stack testID="legal-agreement">
      <InlineText runs={runs} variant="caption" color="text.tertiary" selectable={false} />
    </Stack>
  );
}

/** Settings → Account: links to the Terms of Service and Privacy Policy. */
export function LegalLinks() {
  const t = useT();
  const links = useLegalLinks();
  return (
    <Stack gap="1" testID="legal-links">
      <Text variant="caption" weight="semibold" color="text.tertiary">
        {t("legal.title")}
      </Text>
      <InlineText runs={[links.terms, { text: " · " }, links.privacy]} variant="footnote" color="text.secondary" selectable={false} />
    </Stack>
  );
}
